import Vue from 'vue';
import {HttpOptions} from 'vue-resource/types/vue_resource';
import * as config from '@/utils/config'
import {Project, ProjectPerformance, ProjectPerformanceSimple, SalesProject} from '@/models/project'
import {ProjectsParams} from "@/models/projects-params";
import {ProjectVersion} from "@/models/project-version";
import {OidcService} from '@/services/oidc-service';
import { TimeChartData } from '@/models/chart-data';
import moment from 'moment';
import { Initiative } from '@/models/issue';

export class ProjectService extends Vue {
    private oidcService: OidcService = new OidcService();

    options: HttpOptions = {
        headers: {
            'Authorization': ''
        },
    };

    constructor() {
        super();
    }

    public async getProjects(filters: ProjectsParams): Promise<{data: Project[]; meta: {totalSize: number}}> {
        const access_token =  await this.oidcService.getAccessToken();
        this.options.headers['Authorization'] = 'Bearer ' + access_token;
        return new Promise((resolve, reject) => {
            this.$http.get(config.REDANKA_BE_BASE_URL + '/projects' + this.buildParamsForFilters(filters), this.options)
                .then(
                    (response) => {
                        resolve(response.data);
                    },
                    (error) => {
                        console.error('Error: ', error);
                        reject(error);
                    });
        });
    }

    public async getProjectById(projectId: string): Promise<Project> {
        const access_token =  await this.oidcService.getAccessToken();
        this.options.headers['Authorization'] = 'Bearer ' + access_token;
        return new Promise<Project>((resolve, reject) => {
            this.$http.get(config.REDANKA_BE_BASE_URL + '/projects/' + projectId, this.options)
                .then(
                    (response) => {
                        resolve(response.data);
                    },
                    (error) => {
                        console.error('Error: ', error);
                        reject(error);
                    });
        });
    }

    public async getProjectPerformanceTimeChartDataById(projectId: string): Promise<TimeChartData> {
        const access_token =  await this.oidcService.getAccessToken();
        this.options.headers['Authorization'] = 'Bearer ' + access_token;
        return new Promise<TimeChartData>((resolve, reject) => {
            this.$http.get(config.REDANKA_BE_BASE_URL + '/projects/' + projectId + '/performanceChart', this.options)
                .then(
                    (response) => {
                        resolve(response.data);
                    },
                    (error) => {
                        console.error('Error: ', error);
                        reject(error);
                    });
        });
    }

    public async getProjectPerformanceById(projectId: string): Promise<ProjectPerformance> {
        const access_token =  await this.oidcService.getAccessToken();
        this.options.headers['Authorization'] = 'Bearer ' + access_token;
        return new Promise<ProjectPerformance>((resolve, reject) => {
            this.$http.get(config.REDANKA_BE_BASE_URL + '/projects/' + projectId + '/performance', this.options)
                .then(
                    (response) => {
                        resolve(response.data);
                    },
                    (error) => {
                        console.error('Error: ', error);
                        reject(error);
                    });
        });
    }

    public async getSalesProjects(): Promise<{data: SalesProject[]; meta: {totalSize: number}}> {
        const access_token =  await this.oidcService.getAccessToken();
        this.options.headers['Authorization'] = 'Bearer ' + access_token;
        return new Promise((resolve, reject) => {
            this.$http.get(config.REDANKA_BE_BASE_URL + '/projects/sales', this.options)
                .then(
                    (response) => {
                        resolve(response.data);
                    },
                    (error) => {
                        console.error('Error: ', error);
                        reject(error);
                    });
        });
    }

    public async getProjectChildren(parentId: string): Promise<{data: Project[]; meta: {totalSize: number}}> {
        const access_token =  await this.oidcService.getAccessToken();
        this.options.headers['Authorization'] = 'Bearer ' + access_token;
        return new Promise((resolve, reject) => {
            this.$http.get(config.REDANKA_BE_BASE_URL + '/projects/' + parentId + '/children', this.options)
                .then(
                    (response) => {
                        resolve(response.data);
                    },
                    (error) => {
                        console.error('Error: ', error);
                        reject(error);
                    });
        });
    }

    public async getProjectVersions(projectId: string): Promise<{data: ProjectVersion[]; meta: {totalSize: number}}> {
        const access_token =  await this.oidcService.getAccessToken();
        this.options.headers['Authorization'] = 'Bearer ' + access_token;
        return new Promise((resolve, reject) => {
            this.$http.get(config.REDANKA_BE_BASE_URL + '/projects/' + projectId + '/versions', this.options)
                .then(
                    (response) => {
                        resolve(response.data);
                    },
                    (error) => {
                        console.error('Error: ', error);
                        reject(error);
                    });
        });
    }

    public async getProjectHistory(projectId: string): Promise<{data: Project[]; meta: {totalSize: number}}> {
        const access_token =  await this.oidcService.getAccessToken();
        this.options.headers['Authorization'] = 'Bearer ' + access_token;
        return new Promise((resolve, reject) => {
            this.$http.get(config.REDANKA_BE_BASE_URL + '/projects/' + projectId + '/history', this.options)
                .then(
                    (response) => {
                        resolve(response.data);
                    },
                    (error) => {
                        console.error('Error: ', error);
                        reject(error);
                    });
        });
    }

    public async getProjectPerformances(startDate: Date, endDate: Date, groupBy: string, name: string, bUnit: string, pType: string): Promise<ProjectPerformanceSimple[]> {
        const access_token =  await this.oidcService.getAccessToken();
        this.options.headers['Authorization'] = 'Bearer ' + access_token;
        let params = "?";
        params = params + "startDate=" + moment(startDate).format("YYYY-MM-DD");
        params = params + "&endDate=" + moment(endDate).format("YYYY-MM-DD");
        params = params + "&groupBy=" + groupBy;
        params = params + "&name=" + name;
        params = params + "&bUnit=" + bUnit;
        params = params + "&pType=" + pType;
        return new Promise<ProjectPerformanceSimple[]>((resolve, reject) => {
            this.$http.get(config.REDANKA_BE_BASE_URL + '/projects/performancesInterval'+params, this.options)
                .then(
                    (response) => {
                        resolve(response.data);
                    },
                    (error) => {
                        console.error('Error: ', error);
                        reject(error);
                    });
        });
    }

    public buildParamsForFilters(filters: ProjectsParams): string {
        let params = '?';
        if (filters.root) {
            params += 'root=' + filters.root + '&';
        }

        return encodeURI(params.slice(0, -1));
    }

    public async getProjectsBUnit(): Promise<string[]>{
        return this.getAllCustomValueByFieldName('Business Unit');
    }

    public async getTypeOptions(): Promise<string[]>{
        return this.getAllCustomValueByFieldName('Tipologia Progetto');
    }

    public async getAllCustomValueByFieldName(fieldName: string): Promise<string[]>{
        const access_token =  await this.oidcService.getAccessToken();
        this.options.headers['Authorization'] = 'Bearer ' + access_token;
        return new Promise<string[]>((resolve, reject) => {
            this.$http.get(config.REDANKA_BE_BASE_URL + '/projects/allCustomValuesByFieldName?fieldName='+fieldName, this.options)
                .then(
                    (response) => {
                        resolve(response.data);
                    },
                    (error) => {
                        console.error('Error: ', error);
                        reject(error);
                    });
        });
    }

    public async getInitiative(alsoClosed: boolean, onlyActive: boolean, startDate: Date, endDate: Date, name: string, bUnit: string,selectedStatuses: Array<number>): Promise<Initiative[]> {
        const access_token = await this.oidcService.getAccessToken();
        this.options.headers['Authorization'] = 'Bearer ' + access_token;
        const mapParams = new Map<string, any>();
        mapParams.set("alsoClosed", String(alsoClosed));
        mapParams.set("onlyActive", String(onlyActive));
        mapParams.set("startDate", moment(startDate).format("YYYY-MM-DD"));
        mapParams.set("endDate", moment(endDate).format("YYYY-MM-DD"));
        mapParams.set("name", name);
        mapParams.set("bUnit", bUnit);
        let params = this.buildStringParams(mapParams)
        selectedStatuses.forEach(s => {
            params = params + "&bpStatuses=" + s;
        })
        return new Promise<Initiative[]>((resolve, reject) => {
            this.$http.get(config.REDANKA_BE_BASE_URL + '/projects/getInitiative' + params, this.options)
                .then(
                    (response) => {
                        resolve(response.data);
                    },
                    (error) => {
                        console.error('Error: ', error);
                        reject(error);
                    });
        });
    }

    private buildStringParams(mapParams: Map<string, string>) {
        let paramsString = "?";
        mapParams.forEach((value: string, key: string) => {
            paramsString = paramsString + key + "=" + value;
            paramsString = paramsString + "&"
        });
        return encodeURI(paramsString.slice(0, -1));
    }
}
